import React, { useState, useEffect, useCallback } from "react";

/* ============================================================
   로딱이네 — 관리자 페이지 (/admin)
   - 로그인: POST /api/lotto/admin/login { password } → { token }
   - 등록:   POST /api/lotto/admin/add-draw (X-Admin-Token 헤더)
   - 조회:   GET  /api/lotto/flow?limit=5 (최근 등록 확인용, 공개 API 재사용)
   ============================================================ */

const API_BASE = "/api/lotto";
const LS_ADMIN_TOKEN = "roddaki.adminToken";

const store = {
  get(key) { try { return window.localStorage.getItem(key); } catch { return null; } },
  set(key, v) { try { window.localStorage.setItem(key, v); } catch { /* 무시 */ } },
  remove(key) { try { window.localStorage.removeItem(key); } catch { /* 무시 */ } },
};

/* ---------- 로또 볼 색상 (동행복권 표준) ---------- */
const ballColor = (n) =>
  n <= 10 ? "#FBC400" : n <= 20 ? "#69C8F2" : n <= 30 ? "#FF7272" : n <= 40 ? "#AAAAAA" : "#B0D840";

const Ball = ({ n, size = 30 }) => (
  <span
    style={{
      width: size, height: size, lineHeight: `${size}px`,
      background: ballColor(n), color: "#fff",
      fontSize: size * 0.44, fontWeight: 700,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      borderRadius: "50%", boxShadow: "inset -3px -4px 6px rgba(0,0,0,.18)",
    }}
  >
    {String(n).padStart(2, "0")}
  </span>
);

/* ---------- 가장 최근 토요일 (추첨일 기본값) ---------- */
function lastSaturday() {
  const d = new Date();
  const diff = (d.getDay() + 1) % 7; // 토=6 → 0, 일=0 → 1, 월=1 → 2 ...
  d.setDate(d.getDate() - diff);
  return d.toISOString().slice(0, 10);
}

/* ============================================================
   로그인 화면
   ============================================================ */
function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!password || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) throw new Error(res.status === 401 ? "비밀번호가 올바르지 않습니다." : `오류 (${res.status})`);
      const data = await res.json();
      onLogin(data.token);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8">
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">🐥</div>
          <h1 className="text-xl font-extrabold text-gray-900">로딱이네 관리자</h1>
          <p className="text-sm text-gray-400 mt-1">관리자 비밀번호를 입력하세요</p>
        </div>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="비밀번호"
          className="w-full border border-gray-200 rounded-xl px-4 py-3 font-medium focus:outline-none focus:border-teal-400"
          autoFocus
        />
        {error && <p className="mt-3 text-sm font-bold text-red-500">{error}</p>}
        <button
          onClick={submit}
          disabled={!password || loading}
          className="mt-4 w-full py-3 rounded-xl bg-teal-500 hover:bg-teal-600 text-white font-extrabold disabled:opacity-40"
        >
          {loading ? "확인 중…" : "로그인"}
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   당첨번호 등록 대시보드
   ============================================================ */
function Dashboard({ token, onLogout }) {
  const [recent, setRecent] = useState([]);
  const [round, setRound] = useState("");
  const [nums, setNums] = useState(["", "", "", "", "", ""]);
  const [bonus, setBonus] = useState("");
  const [drawDate, setDrawDate] = useState(lastSaturday());
  const [msg, setMsg] = useState(null);       // { type: 'ok'|'err', text }
  const [saving, setSaving] = useState(false);

  /* 최근 회차 로드 + 다음 회차 자동 제안 */
  const loadRecent = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/flow?limit=5`);
      if (!res.ok) return;
      const rows = await res.json();
      setRecent(rows);
      if (rows.length > 0) setRound(String(rows[0].round + 1));
    } catch { /* 무시 */ }
  }, []);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const setNum = (i, v) => {
    const next = [...nums];
    next[i] = v.replace(/[^0-9]/g, "").slice(0, 2);
    setNums(next);
  };

  /* 유효성 검사: 통과 시 null, 실패 시 메시지 반환 */
  const validate = () => {
    const n = nums.map(Number);
    const b = Number(bonus);
    if (!round || Number(round) < 1) return "회차를 입력하세요.";
    if (nums.some((v) => v === "")) return "당첨번호 6개를 모두 입력하세요.";
    if (n.some((v) => v < 1 || v > 45)) return "당첨번호는 1~45 사이여야 합니다.";
    if (new Set(n).size !== 6) return "당첨번호에 중복이 있습니다.";
    if (!bonus || b < 1 || b > 45) return "보너스 번호는 1~45 사이여야 합니다.";
    if (n.includes(b)) return "보너스 번호가 당첨번호와 중복됩니다.";
    if (!drawDate) return "추첨일을 입력하세요.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { setMsg({ type: "err", text: err }); return; }
    if (saving) return;

    const sorted = nums.map(Number).sort((a, b) => a - b);
    if (!window.confirm(
      `${round}회차로 등록합니다.\n번호: ${sorted.join(" ")} + 보너스 ${bonus}\n추첨일: ${drawDate}\n맞습니까?`
    )) return;

    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/admin/add-draw`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": token,
        },
        body: JSON.stringify({
          round: Number(round),
          nums: sorted,
          bonus: Number(bonus),
          drawDate,
        }),
      });
      if (res.status === 401 || res.status === 403) {
        onLogout(); // 토큰 만료 → 재로그인
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `등록 실패 (${res.status})`);
      }
      setMsg({ type: "ok", text: `${round}회차 등록 완료!` });
      setNums(["", "", "", "", "", ""]);
      setBonus("");
      loadRecent();   // 목록 갱신 + 다음 회차 재제안
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* 헤더 */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <span className="font-extrabold text-teal-600">🐥 로딱이네 관리자</span>
          <button onClick={onLogout} className="text-sm font-bold text-gray-400 hover:text-gray-600">
            로그아웃
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        {/* 등록 폼 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
          <h2 className="text-lg font-extrabold text-gray-800 mb-5">당첨번호 등록</h2>

          <div className="grid grid-cols-2 gap-4 mb-5">
            <label className="block">
              <span className="text-xs font-bold text-gray-500">회차</span>
              <input value={round} onChange={(e) => setRound(e.target.value.replace(/[^0-9]/g, ""))}
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 font-bold" />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-gray-500">추첨일</span>
              <input type="date" value={drawDate} onChange={(e) => setDrawDate(e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 font-medium" />
            </label>
          </div>

          <span className="text-xs font-bold text-gray-500">당첨번호 6개</span>
          <div className="mt-1 mb-4 grid grid-cols-6 gap-2">
            {nums.map((v, i) => (
              <input key={i} value={v} onChange={(e) => setNum(i, e.target.value)}
                inputMode="numeric" placeholder={`${i + 1}번`}
                className="border border-gray-200 rounded-lg px-2 py-3 text-center font-bold" />
            ))}
          </div>

          <label className="block w-28">
            <span className="text-xs font-bold text-gray-500">보너스</span>
            <input value={bonus} onChange={(e) => setBonus(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
              inputMode="numeric"
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-center font-bold" />
          </label>

          {msg && (
            <p className={`mt-4 text-sm font-bold ${msg.type === "ok" ? "text-teal-600" : "text-red-500"}`}>
              {msg.text}
            </p>
          )}

          <button onClick={submit} disabled={saving}
            className="mt-5 w-full py-3.5 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-lg font-extrabold disabled:opacity-40">
            {saving ? "등록 중…" : "등록"}
          </button>
        </div>

        {/* 최근 등록 회차 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
          <h2 className="text-lg font-extrabold text-gray-800 mb-4">최근 등록 회차</h2>
          {recent.length === 0 ? (
            <p className="text-gray-400 text-sm">불러오는 중…</p>
          ) : (
            <div className="space-y-2.5">
              {recent.map((r) => (
                <div key={r.round} className="flex items-center gap-3">
                  <span className="w-14 font-bold text-gray-600">{r.round}회</span>
                  <span className="flex gap-1.5">{r.nums.map((n) => <Ball key={n} n={n} />)}</span>
                  <span className="text-gray-300 font-bold">+</span>
                  <Ball n={r.bonus} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   AdminApp — 토큰 유무로 로그인/대시보드 분기
   ============================================================ */
export default function AdminApp() {
  const [token, setToken] = useState(() => store.get(LS_ADMIN_TOKEN));

  const handleLogin = (t) => {
    store.set(LS_ADMIN_TOKEN, t);
    setToken(t);
  };
  const handleLogout = () => {
    store.remove(LS_ADMIN_TOKEN);
    setToken(null);
  };

  return (
    <div style={{ fontFamily: "'Pretendard','Apple SD Gothic Neo','Noto Sans KR',sans-serif" }}>
      {token ? <Dashboard token={token} onLogout={handleLogout} /> : <LoginScreen onLogin={handleLogin} />}
    </div>
  );
}
